"use client";

import { useState, useRef, useMemo, useEffect } from "react";
import Map, {
  Marker,
  NavigationControl,
  Popup,
  MapRef,
  Source,
  Layer,
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { motion, AnimatePresence } from "framer-motion";
import { HiLocationMarker } from "react-icons/hi";
import type { LineLayer } from "react-map-gl";
import type {
  FeatureCollection,
  Feature,
  LineString,
  GeoJsonProperties,
} from "geojson";
import * as turf from "@turf/turf";
import { MAIN_COORDINATES } from "@/data/location";
import { useLanguage } from "@/lib/i18n";
import { COLORS } from "@/lib/design-tokens";

// ==========================================
// 1. KOMPONEN MARKER (INTERNAL)
// ==========================================

// Marker Utama (StayEase)
const MainMarker = ({
  isActive,
  onClick,
}: {
  isActive: boolean;
  onClick: (e: React.MouseEvent) => void;
}) => {
  return (
    <div
      className={`relative flex items-center justify-center w-12 h-12 cursor-pointer group z-20 transition-all duration-500 ${
        isActive ? "scale-90 opacity-80" : "scale-110"
      }`}
      onClick={onClick}
    >
      {!isActive && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-gold opacity-20"></span>
      )}
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="relative z-10"
      >
        <HiLocationMarker
          size={48}
          fill={COLORS.primary.DEFAULT}
          className="text-brand-charcoal drop-shadow-xl"
        />
        <div className="absolute top-[14px] left-[18px] w-3 h-3 bg-white rounded-full shadow-inner" />
      </motion.div>
      <div className="absolute bottom-0 w-4 h-1 bg-black/30 blur-[2px] rounded-[100%]" />
    </div>
  );
};

// Marker Lokasi Sekitar
const NearbyMarker = ({
  name,
  Icon,
  isActive,
  onClick,
}: {
  name: string;
  Icon: any;
  isActive: boolean;
  onClick: (e: React.MouseEvent) => void;
}) => {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: isActive ? 1.1 : 1 }}
      exit={{ scale: 0 }}
      className="relative flex flex-col items-center cursor-pointer z-10"
      onClick={onClick}
    >
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md border transition-all duration-500 ${
          isActive
            ? "bg-brand-charcoal border-brand-gold text-brand-gold scale-110"
            : "bg-white border-gray-200 text-gray-500 hover:text-brand-charcoal hover:scale-110"
        }`}
      >
        <Icon size={18} />
      </div>

      <div
        className={`mt-2 px-3 py-1 bg-white/95 backdrop-blur-sm rounded-md shadow-sm border border-gray-100 transition-all duration-300 absolute top-10 whitespace-nowrap z-50 pointer-events-none ${
          isActive
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0"
        }`}
      >
        <span className="text-[10px] font-bold uppercase tracking-wider text-brand-charcoal">
          {name}
        </span>
      </div>
    </motion.div>
  );
};

// ==========================================
// 2. LOGIC & HELPER PETA
// ==========================================

interface LocationItem {
  id: number;
  name: string;
  dist: string;
  coords: { lat: number; lng: number; zoom: number };
  icon: any;
}

interface StayEaseMapProps {
  locations: LocationItem[];
  activeLocationId: number | null;
  onLocationSelect: (id: number | null) => void;
  className?: string;
}

const radiatingLineStyle: LineLayer = {
  id: "radiating-lines",
  type: "line",
  paint: {
    "line-color": COLORS.primary.DEFAULT,
    "line-width": 1.5,
    "line-opacity": 0.3,
    "line-dasharray": [2, 4],
  },
  layout: { "line-cap": "round", "line-join": "round" },
};

const activeLineStyle: LineLayer = {
  id: "active-line",
  type: "line",
  paint: {
    "line-color": COLORS.primary.DEFAULT,
    "line-width": 3,
    "line-opacity": 0.9,
  },
  layout: { "line-cap": "round", "line-join": "round" },
};

// Komponen Label Estimasi Waktu (akan dirender sebagai Popup)
const TimeEstimatePopupContent = ({
  location,
  progress,
  language,
}: {
  location: LocationItem;
  progress: number;
  language: "id" | "en";
}) => {
  const fromText = language === "id" ? "dari KinKen" : "from KinKen";
  const travelText = language === "id" ? "Perjalanan" : "Travel";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ 
        opacity: progress > 0.3 ? 1 : progress * 3,
        scale: 1,
      }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="pointer-events-none"
    >
      <div className="bg-brand-charcoal/95 backdrop-blur-md rounded-xl px-5 py-3 shadow-2xl border border-brand-gold/30 min-w-[200px]">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-7 h-7 rounded-full bg-brand-gold flex items-center justify-center shrink-0">
            <location.icon size={14} className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-serif text-xs font-medium leading-tight truncate">
              {location.name}
            </p>
            <p className="text-gray-400 text-[8px] uppercase tracking-widest">
              {fromText}
            </p>
          </div>
        </div>
        
        {/* Divider with animation */}
        <div className="relative h-[2px] bg-gray-700 rounded-full overflow-hidden mb-2">
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: `${progress * 100}%` }}
            transition={{ duration: 0.1 }}
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-brand-gold to-[#C4B49A]"
          />
        </div>
        
        {/* Time Display */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
            <span className="text-gray-400 text-[10px] font-sans">{travelText}</span>
          </div>
          <motion.span 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-brand-gold font-bold text-lg font-serif"
          >
            {location.dist}
          </motion.span>
        </div>
      </div>
    </motion.div>
  );
};

const getCurvedPath = (start: any, end: any): number[][] => {
  const startPt = turf.point([start.lng, start.lat]);
  const endPt = turf.point([end.lng, end.lat]);
  const options = { npoints: 100, offset: 10 };
  const path = turf.greatCircle(startPt, endPt, options);

  // Pastikan formatnya benar
  const coords = path.geometry.coordinates;

  // Jika coords sudah number[][], langsung return
  if (Array.isArray(coords) && Array.isArray(coords[0])) {
    return coords as number[][];
  }

  // Fallback ke garis lurus
  return [
    [start.lng, start.lat],
    [end.lng, end.lat],
  ];
};

// ==========================================
// 3. KOMPONEN UTAMA (DEFAULT EXPORT)
// ==========================================

export default function StayEaseMap({
  locations,
  activeLocationId,
  onLocationSelect,
  className,
}: StayEaseMapProps) {
  const { language } = useLanguage();
  const mapRef = useRef<MapRef>(null);
  const [showPopup, setShowPopup] = useState(true);
  const [drawProgress, setDrawProgress] = useState(0);
  const animationRef = useRef<number | null>(null);

  // Hitung Jalur (Path)
  const fullCurvedPaths = useMemo(() => {
    const paths: Record<number, number[][]> = {};
    locations.forEach((place) => {
      paths[place.id] = getCurvedPath(
        { lng: MAIN_COORDINATES.longitude, lat: MAIN_COORDINATES.latitude },
        { lng: place.coords.lng, lat: place.coords.lat },
      );
    });
    return paths;
  }, [locations]);

  // Animasi Progress Garis
  useEffect(() => {
    if (activeLocationId !== null) {
      setDrawProgress(0);
      let startTimestamp: number | null = null;
      const duration = 2000;
      const animate = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const elapsed = timestamp - startTimestamp;
        const progress = Math.min(1 - Math.pow(1 - elapsed / duration, 3), 1);
        setDrawProgress(progress);
        if (progress < 1) animationRef.current = requestAnimationFrame(animate);
      };
      animationRef.current = requestAnimationFrame(animate);
    } else {
      setDrawProgress(0);
    }
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [activeLocationId]);

  // FlyTo Logic
  useEffect(() => {
    if (activeLocationId === null) {
      mapRef.current?.flyTo({
        center: [MAIN_COORDINATES.longitude, MAIN_COORDINATES.latitude],
        zoom: MAIN_COORDINATES.zoom,
        pitch: MAIN_COORDINATES.pitch,
        bearing: MAIN_COORDINATES.bearing,
        duration: 2000,
      });
      setShowPopup(true);
    } else {
      const target = locations.find((l) => l.id === activeLocationId);
      if (target) {
        mapRef.current?.flyTo({
          center: [target.coords.lng, target.coords.lat],
          zoom: target.coords.zoom || 16,
          pitch: 50,
          bearing: 20,
          duration: 2000,
        });
        setShowPopup(false);
      }
    }
  }, [activeLocationId, locations]);

  // GeoJSON Logic
  const radiatingGeoJSON = useMemo((): FeatureCollection | null => {
    if (activeLocationId !== null) return null;

    return {
      type: "FeatureCollection",
      features: locations.map((place) => ({
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: fullCurvedPaths[place.id] || [],
        },
      })) as Feature<LineString, GeoJsonProperties>[],
    };
  }, [activeLocationId, fullCurvedPaths, locations]);

  const activeAnimatedGeoJSON = useMemo((): Feature<LineString> | null => {
    if (activeLocationId === null || !fullCurvedPaths[activeLocationId])
      return null;
    const fullPath = fullCurvedPaths[activeLocationId];
    const endIndex = Math.floor(fullPath.length * drawProgress);
    const animatedCoordinates = fullPath.slice(0, endIndex + 1);
    if (animatedCoordinates.length < 2) return null;

    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: animatedCoordinates,
      },
    } as Feature<LineString>;
  }, [activeLocationId, drawProgress, fullCurvedPaths]);

  // Dapatkan active location untuk overlay
  const activeLocation = activeLocationId 
    ? locations.find((l) => l.id === activeLocationId) || null 
    : null;

  return (
    <div
      className={`relative w-full h-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 ${className}`}
    >
      <Map
        ref={mapRef}
        initialViewState={MAIN_COORDINATES}
        mapStyle="mapbox://styles/mapbox/light-v11"
        mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN}
        scrollZoom={false}
        attributionControl={false}
      >
        <NavigationControl position="bottom-right" showCompass={false} />
        {/* LAYERS */}
        {radiatingGeoJSON && (
          <Source id="radiating-source" type="geojson" data={radiatingGeoJSON}>
            <Layer {...radiatingLineStyle} />
          </Source>
        )}
        {activeAnimatedGeoJSON && (
          <Source
            id="active-source"
            type="geojson"
            data={activeAnimatedGeoJSON}
          >
            <Layer {...activeLineStyle} />
            <Layer
              id="line-end-point"
              type="circle"
              paint={{
                "circle-radius": 5,
                "circle-color": COLORS.primary.DEFAULT,
                "circle-stroke-width": 2,
                "circle-stroke-color": "#ffffff",
              }}
              filter={["==", ["geometry-type"], "Point"]}
            />
          </Source>
        )}
        {/* MAIN MARKER */}
        <Marker
          latitude={MAIN_COORDINATES.latitude}
          longitude={MAIN_COORDINATES.longitude}
          anchor="bottom"
        >
          <MainMarker
            isActive={activeLocationId !== null}
            onClick={(e) => {
              e.stopPropagation();
              onLocationSelect(null);
            }}
          />
        </Marker>
        {/* NEARBY MARKERS */}
        {locations.map((place) => {
          const isActive = activeLocationId === place.id;
          const shouldShow = isActive || activeLocationId === null;
          return shouldShow ? (
            <Marker
              key={place.id}
              latitude={place.coords.lat}
              longitude={place.coords.lng}
              anchor="bottom"
            >
              <NearbyMarker
                name={place.name}
                Icon={place.icon}
                isActive={isActive}
                onClick={(e) => {
                  e.stopPropagation();
                  onLocationSelect(place.id);
                }}
              />
            </Marker>
          ) : null;
        })}
        
        {/* TIME ESTIMATE POPUP AT LOCATION MARKER */}
        <AnimatePresence>
          {activeLocation && drawProgress > 0.1 && (
            <Popup
              latitude={activeLocation.coords.lat}
              longitude={activeLocation.coords.lng}
              anchor="bottom"
              offset={50}
              closeButton={false}
              closeOnClick={false}
              className="time-estimate-popup"
              maxWidth="250px"
            >
              <TimeEstimatePopupContent
                location={activeLocation}
                progress={drawProgress}
                language={language}
              />
            </Popup>
          )}
        </AnimatePresence>
        
        {/* POPUP */}
        <AnimatePresence>
          {showPopup && activeLocationId === null && (
            <Popup
              latitude={MAIN_COORDINATES.latitude}
              longitude={MAIN_COORDINATES.longitude}
              anchor="top"
              offset={25}
              onClose={() => setShowPopup(false)}
              closeButton={false}
              className="custom-popup-clean"
              maxWidth="200px"
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                className="p-3 text-center backdrop-blur-md bg-white/90 rounded-lg shadow-sm border border-white/50"
              >
                <p className="font-serif text-brand-charcoal text-sm font-bold tracking-wide">
                  KinKen Kost Elite
                </p>
                <div className="h-[1px] w-8 bg-brand-gold mx-auto my-2"></div>
                <p className="text-gray-500 text-[10px] uppercase tracking-widest">
                  Gg. Mawar No.8, Sidakarya
                </p>
              </motion.div>
            </Popup>
          )}
        </AnimatePresence>
      </Map>

      <style jsx global>{`
        .mapboxgl-popup-content {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }
        .mapboxgl-popup-tip {
          border-bottom-color: rgba(255, 255, 255, 0.9) !important;
        }
        .time-estimate-popup .mapboxgl-popup-tip {
          border-top-color: rgba(26, 26, 26, 0.95) !important;
          border-bottom-color: transparent !important;
        }
      `}</style>
    </div>
  );
}
